import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const getToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('adminSession'))?.token || null;
  } catch (e) {
    return null;
  }
};
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const errText = (err, fallback) => err?.response?.data?.message || fallback;

function formatDate(v) {
  if (!v) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [y, m, d] = v.slice(0, 10).split('-');
  return `${d} ${months[Number(m) - 1]} ${y}`;
}

function AdminCertificateManager() {
  const [tab, setTab] = useState('issue');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 6000); };

  // ----- issue tab -----
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [student, setStudent] = useState(null);
  const [courseId, setCourseId] = useState('');
  const [allCourses, setAllCourses] = useState([]);
  const [pronoun, setPronoun] = useState('Their');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastIssued, setLastIssued] = useState(null);

  // ----- issued tab -----
  const [issued, setIssued] = useState([]);
  const [issuedSearch, setIssuedSearch] = useState('');

  // ----- settings tab -----
  const [settings, setSettings] = useState({ number_format: '', next_number: 1, default_description: '', example: '' });
  const [courseTexts, setCourseTexts] = useState([]);
  const [editCourse, setEditCourse] = useState('');
  const [editHeading, setEditHeading] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${apiUrl}/api/admin/certificate-manager/settings`, auth()),
        axios.get(`${apiUrl}/api/admin/certificate-manager/course-texts`, auth()),
      ]);
      setSettings(s.data.settings);
      setCourseTexts(c.data.courses || []);
      setAllCourses(c.data.courses || []);
    } catch (err) {
      setError(errText(err, 'Could not load certificate settings.'));
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // student search (debounced)
  useEffect(() => {
    if (tab !== 'issue') return undefined;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${apiUrl}/api/admin/certificate-manager/students`, { ...auth(), params: { search } });
        setStudents(r.data.students || []);
      } catch (err) {
        setError(errText(err, 'Could not search students.'));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, tab]);

  const loadIssued = useCallback(async () => {
    try {
      const r = await axios.get(`${apiUrl}/api/admin/certificate-manager/issued`, { ...auth(), params: { search: issuedSearch } });
      setIssued(r.data.certificates || []);
    } catch (err) {
      setError(errText(err, 'Could not load issued certificates.'));
    }
  }, [issuedSearch]);

  useEffect(() => {
    if (tab !== 'issued') return undefined;
    const t = setTimeout(loadIssued, 300);
    return () => clearTimeout(t);
  }, [tab, loadIssued]);

  // pre-fill the wording whenever the course or pronoun changes
  const loadDraft = useCallback(async (sid, cid, pr) => {
    if (!sid || !cid) { setTitle(''); setDescription(''); return; }
    try {
      const r = await axios.get(`${apiUrl}/api/admin/certificate-manager/draft`, { ...auth(), params: { student_id: sid, course_id: cid, pronoun: pr } });
      setTitle(r.data.title || '');
      setDescription(r.data.description || '');
    } catch (err) {
      setError(errText(err, 'Could not build the certificate wording.'));
    }
  }, []);

  useEffect(() => { loadDraft(student?.id, courseId, pronoun); }, [student, courseId, pronoun, loadDraft]);

  const chooseStudent = (s) => {
    setError('');
    setLastIssued(null);
    setStudent(s);
    setCourseId(s.courses.length === 1 ? String(s.courses[0].course_id) : '');
  };

  const payload = () => ({
    student_id: student.id,
    course_id: Number(courseId),
    issue_date: issueDate,
    pronoun,
    title,
    description,
  });

  const preview = async () => {
    setError('');
    setBusy(true);
    try {
      const r = await axios.post(`${apiUrl}/api/admin/certificate-manager/preview`, payload(), { ...auth(), responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (err) {
      // blob error bodies need decoding to show the message
      let msg = 'Could not create the preview.';
      try { msg = JSON.parse(await err.response.data.text()).message || msg; } catch (e) { /* keep default */ }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const issue = async () => {
    setError('');
    const existing = student.courses.find((c) => String(c.course_id) === String(courseId))?.certificates || [];
    const warn = existing.length ? `\n\nNote: this student already has certificate ${existing.join(', ')} for this course.` : '';
    if (!window.confirm(`Issue a certificate to ${student.firstName} ${student.lastName}?${warn}\n\nA new certificate number will be used.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${apiUrl}/api/admin/certificate-manager/issue`, payload(), auth());
      setLastIssued(r.data);
      flash(`Certificate ${r.data.certificate_number} issued and saved.`);
      setStudent(null);
      setCourseId('');
      setSearch('');
      loadSettings();
    } catch (err) {
      setError(errText(err, 'Could not issue the certificate.'));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.put(`${apiUrl}/api/admin/certificate-manager/settings`, settings, auth());
      flash('Settings saved.');
      loadSettings();
    } catch (err) {
      setError(errText(err, 'Could not save settings.'));
    }
  };

  const pickEditCourse = (id) => {
    setEditCourse(id);
    const c = courseTexts.find((x) => String(x.course_id) === String(id));
    setEditHeading(c?.heading || '');
    setEditDescription(c?.description || '');
  };

  const saveCourseText = async () => {
    setError('');
    try {
      await axios.put(`${apiUrl}/api/admin/certificate-manager/course-texts/${editCourse}`, { heading: editHeading, description: editDescription }, auth());
      flash('Course wording saved.');
      loadSettings();
    } catch (err) {
      setError(errText(err, 'Could not save course wording.'));
    }
  };

  const selectedCourse = student?.courses.find((c) => String(c.course_id) === String(courseId));
  const courseOptions = student ? (student.courses.length ? student.courses : null) : null;
  const canAct = student && courseId && issueDate && title.trim() && description.trim() && !busy;

  const tabBtn = (id, label) => (
    <button type="button" className={`btn rounded-pill py-9 px-20 ${tab === id ? 'btn-main' : 'btn-outline-main'}`} onClick={() => { setTab(id); setError(''); }}>
      {label}
    </button>
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb mb-24">
            <ul className="flex-align gap-4">
              <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
              <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 fw-normal text-15">Certificate Manager</span></li>
            </ul>
          </div>

          <div className="d-flex gap-8 flex-wrap mb-24">
            {tabBtn('issue', 'Issue Certificate')}
            {tabBtn('issued', 'Issued Certificates')}
            {tabBtn('settings', 'Settings')}
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {lastIssued && tab === 'issue' && (
            <div className="alert alert-success mb-20 d-flex flex-between flex-wrap gap-8">
              <span>Certificate <strong>{lastIssued.certificate_number}</strong> was generated and stored on the server.</span>
              <a className="btn btn-sm btn-main rounded-pill" href={`${apiUrl}/${lastIssued.file_path}`} target="_blank" rel="noreferrer">
                <i className="ph ph-download-simple me-2" /> Open PDF
              </a>
            </div>
          )}

          {/* ---------------- ISSUE ---------------- */}
          {tab === 'issue' && (
            <div className="row g-24">
              <div className="col-lg-5">
                <div className="card">
                  <div className="card-header border-bottom border-gray-100"><h5 className="mb-0">1. Find the student</h5></div>
                  <div className="card-body">
                    <input
                      type="text"
                      className="form-control py-9 mb-16"
                      placeholder="Search by name, student ID, email, mobile or course..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {searching && <div className="text-13 text-gray-400 mb-8">Searching…</div>}
                    <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                      {students.length === 0 && !searching && <div className="text-gray-400 text-center py-24">No students found.</div>}
                      {students.map((s) => (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => chooseStudent(s)}
                          className="w-100 text-start border rounded-8 p-12 mb-8 bg-white"
                          style={{ borderColor: student?.id === s.id ? '#198754' : undefined, borderWidth: student?.id === s.id ? 2 : 1 }}
                        >
                          <div className="fw-semibold">{s.firstName} {s.lastName} <span className="text-13 text-gray-400 fw-normal">({s.studentid})</span></div>
                          <div className="text-13 text-gray-500">
                            {s.courses.length ? s.courses.map((c) => c.coursename).join(' • ') : 'No course on record'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="card">
                  <div className="card-header border-bottom border-gray-100"><h5 className="mb-0">2. Certificate details</h5></div>
                  <div className="card-body">
                    {!student ? (
                      <div className="text-gray-400 text-center py-40">Select a student from the list to continue.</div>
                    ) : (
                      <div className="row g-16">
                        <div className="col-12">
                          <div className="p-12 rounded-8 bg-main-25 border">
                            <strong>{student.firstName} {student.lastName}</strong> <span className="text-13 text-gray-500">({student.studentid})</span>
                          </div>
                        </div>
                        <div className="col-md-8">
                          <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                          <select className="form-control py-9" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                            <option value="">Select course…</option>
                            {courseOptions
                              ? courseOptions.map((c) => <option key={c.course_id} value={c.course_id}>{c.coursename}</option>)
                              : allCourses.map((c) => <option key={c.course_id} value={c.course_id}>{c.coursename}</option>)}
                          </select>
                          {!courseOptions && <div className="text-12 text-warning mt-4">This student has no course on record, so all courses are listed.</div>}
                          {selectedCourse?.certificates?.length > 0 && (
                            <div className="text-12 text-warning mt-4">Already issued for this course: {selectedCourse.certificates.join(', ')}</div>
                          )}
                        </div>
                        <div className="col-md-4">
                          <label className="text-13 text-gray-500 mb-4 d-block">Issue date</label>
                          <input type="date" className="form-control py-9" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="text-13 text-gray-500 mb-4 d-block">Wording uses</label>
                          <select className="form-control py-9" value={pronoun} onChange={(e) => setPronoun(e.target.value)}>
                            <option value="His">His</option>
                            <option value="Her">Her</option>
                            <option value="Their">Their (neutral)</option>
                          </select>
                        </div>
                        <div className="col-md-8">
                          <label className="text-13 text-gray-500 mb-4 d-block">Course title on certificate (red)</label>
                          <input type="text" className="form-control py-9" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="text-13 text-gray-500 mb-4 d-block">Description (based on the selected course — editable)</label>
                          <textarea className="form-control" rows={4} maxLength={600} value={description} onChange={(e) => setDescription(e.target.value)} />
                          <div className="text-12 text-gray-400 mt-4">The certificate number is assigned automatically ({settings.example || '…'} is the next one).</div>
                        </div>
                        <div className="col-12 d-flex gap-8 flex-wrap">
                          <button type="button" className="btn btn-outline-main rounded-pill py-9" disabled={!canAct} onClick={preview}>
                            <i className="ph ph-eye me-2" /> Preview PDF
                          </button>
                          <button type="button" className="btn btn-main rounded-pill py-9" disabled={!canAct} onClick={issue}>
                            <i className="ph ph-seal-check me-2" /> {busy ? 'Working…' : 'Issue Certificate'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ---------------- ISSUED ---------------- */}
          {tab === 'issued' && (
            <div className="card">
              <div className="card-header border-bottom border-gray-100 d-flex flex-between flex-wrap gap-8">
                <h5 className="mb-0">Certificates generated here ({issued.length})</h5>
                <input
                  type="text"
                  className="form-control py-9"
                  style={{ maxWidth: 320 }}
                  placeholder="Search number, student, course…"
                  value={issuedSearch}
                  onChange={(e) => setIssuedSearch(e.target.value)}
                />
              </div>
              <div className="card-body p-0">
                {issued.length === 0 ? (
                  <div className="text-center py-40 text-gray-400">No generated certificates yet.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table mb-0">
                      <thead>
                        <tr><th>Certificate No.</th><th>Student</th><th>Course</th><th>Issued</th><th>Status</th><th>PDF</th></tr>
                      </thead>
                      <tbody>
                        {issued.map((c) => (
                          <tr key={c.id}>
                            <td className="fw-semibold">{c.certificate_number}</td>
                            <td>{c.student_name} <span className="text-13 text-gray-400">({c.studentid})</span></td>
                            <td style={{ maxWidth: 260, whiteSpace: 'normal' }}>{c.course_title}</td>
                            <td>{formatDate(c.issued_date)}</td>
                            <td><span className={`badge ${c.status === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>{c.status}</span></td>
                            <td>
                              <a className="btn btn-sm btn-outline-main py-6 px-10" href={`${apiUrl}/${c.file_path}`} target="_blank" rel="noreferrer">
                                <i className="ph ph-download-simple me-1" /> Open
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="card-footer text-13 text-gray-500">
                Revoke or delete a certificate from the <Link to="/admin-certificates">Certificates</Link> page.
              </div>
            </div>
          )}

          {/* ---------------- SETTINGS ---------------- */}
          {tab === 'settings' && (
            <div className="row g-24">
              <div className="col-lg-6">
                <form className="card" onSubmit={saveSettings}>
                  <div className="card-header border-bottom border-gray-100"><h5 className="mb-0">Certificate number</h5></div>
                  <div className="card-body">
                    <label className="text-13 text-gray-500 mb-4 d-block">Number format</label>
                    <input
                      type="text"
                      className="form-control py-9"
                      value={settings.number_format}
                      onChange={(e) => setSettings({ ...settings, number_format: e.target.value })}
                    />
                    <div className="text-12 text-gray-500 mt-8 mb-16">
                      Use <code>{'{SEQ:6}'}</code> for the running number (6 digits, zero-padded), plus <code>{'{YY}'}</code> / <code>{'{YYYY}'}</code> /{' '}
                      <code>{'{MM}'}</code> for the issue date. Example: <code>{'IFM{YY}{SEQ:6}'}</code> gives <strong>{settings.example}</strong>.
                    </div>
                    <label className="text-13 text-gray-500 mb-4 d-block">Next running number</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control py-9 mb-8"
                      value={settings.next_number}
                      onChange={(e) => setSettings({ ...settings, next_number: e.target.value })}
                    />
                    <div className="text-12 text-gray-500 mb-16">It goes up by one with every certificate. Numbers already in use are skipped automatically.</div>
                    <label className="text-13 text-gray-500 mb-4 d-block">Default description</label>
                    <textarea
                      className="form-control mb-8"
                      rows={4}
                      value={settings.default_description}
                      onChange={(e) => setSettings({ ...settings, default_description: e.target.value })}
                    />
                    <div className="text-12 text-gray-500 mb-16">
                      Used for courses without their own wording. Placeholders: <code>{'{pronoun}'}</code> (His/Her/Their), <code>{'{course}'}</code>, <code>{'{student}'}</code>.
                    </div>
                    <button type="submit" className="btn btn-main rounded-pill py-9">Save settings</button>
                  </div>
                </form>
              </div>

              <div className="col-lg-6">
                <div className="card">
                  <div className="card-header border-bottom border-gray-100"><h5 className="mb-0">Wording for a specific course</h5></div>
                  <div className="card-body">
                    <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                    <select className="form-control py-9 mb-16" value={editCourse} onChange={(e) => pickEditCourse(e.target.value)}>
                      <option value="">Select course…</option>
                      {courseTexts.map((c) => (
                        <option key={c.course_id} value={c.course_id}>{c.coursename}{c.heading || c.description ? '  ✓' : ''}</option>
                      ))}
                    </select>
                    {editCourse && (
                      <>
                        <label className="text-13 text-gray-500 mb-4 d-block">Title on certificate (leave empty to use the course name)</label>
                        <input type="text" className="form-control py-9 mb-16" value={editHeading} maxLength={255} onChange={(e) => setEditHeading(e.target.value)} />
                        <label className="text-13 text-gray-500 mb-4 d-block">Description (leave empty to use the default)</label>
                        <textarea className="form-control mb-8" rows={5} maxLength={600} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                        <div className="text-12 text-gray-500 mb-16">Placeholders: <code>{'{pronoun}'}</code>, <code>{'{course}'}</code>, <code>{'{student}'}</code>.</div>
                        <button type="button" className="btn btn-main rounded-pill py-9" onClick={saveCourseText}>Save course wording</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminCertificateManager;
